/**
 * taskStop.ts — stopping the model without orphaning the project's other
 * tasks.
 *
 * Docker execution means tasks outlive the button click: stopping the model
 * leaves the workflow worker, trainers, mlmodel runs, and the notebook
 * running as (from the user's perspective) orphans. This helper owns the
 * polite flow, shared by the MenuBar's transport Stop and the Tasks panel's
 * model row:
 *
 *   1. stop the model;
 *   2. poll the task list — if anything ELSE is still running, ask once
 *      (ConfirmDialog — house rule, never window.confirm) whether to stop
 *      those too;
 *   3. on confirm, stop each through its own dedicated route.
 *
 * Callers pass their own `stopModel` (the App goes through sim.runStop, the
 * Tasks panel through the adapter) and gate the prompt on the model having
 * actually been running.
 */
import type { ConfirmFn } from '../panels/ConfirmDialog';
import type { ProjectTask, RemoteProjectAdapter } from '../projects/RemoteProjectAdapter';

/** Human label for a task row in the prompt's list. */
export function taskLabel(task: ProjectTask): string {
  if (task.kind === 'workflows') return 'Workflows worker';
  if (task.kind === 'notebook') return 'Jupyter notebook';
  if (task.kind === 'trainer') return `Trainer ${task.trainer ?? ''}`.trim();
  if (task.kind === 'mlmodel') return `Model ${task.model ?? ''}`.trim();
  return task.label;
}

/** Stop ONE non-model task through its dedicated route (best-effort: a
 *  failure stops the loop and surfaces to the caller — the task list poll
 *  is the source of truth afterwards). */
export async function stopOtherTask(
  adapter: RemoteProjectAdapter,
  projectId: string,
  task: ProjectTask,
): Promise<void> {
  if (task.kind === 'workflows') {
    await adapter.workflowWorkerStop(projectId);
  } else if (task.kind === 'notebook') {
    await adapter.notebookStop(projectId);
  } else if (task.kind === 'trainer' && task.trainer) {
    await adapter.trainerStop(projectId, task.trainer);
  } else if (task.kind === 'mlmodel' && task.model) {
    await adapter.modelRunStop(projectId, task.model);
  }
}

/** Stop the model; if other tasks keep running, ask whether to stop them
 *  too. No prompt (and no orphan sweep) when nothing else runs, when the
 *  model wasn't running, or when the task poll fails. */
export async function stopModelAndPromptOthers(opts: {
  adapter: RemoteProjectAdapter;
  projectId: string;
  /** Stop the MODEL — the caller's own path (sim.runStop / adapter.runStop). */
  stopModel: () => Promise<unknown>;
  confirm: ConfirmFn;
  /** Only prompt when the model was actually running (the click did
   *  something); defaults to true. */
  modelWasRunning?: boolean;
}): Promise<void> {
  const { adapter, projectId, stopModel, confirm } = opts;
  await stopModel();
  if (opts.modelWasRunning === false) return;
  let tasks: ProjectTask[];
  try {
    tasks = (await adapter.tasks(projectId)).tasks;
  } catch {
    return; // can't know what's running — don't prompt on a guess
  }
  const others = tasks.filter((t) => t.running && t.kind !== 'model');
  if (others.length === 0) return;
  const stopAll = await confirm({
    title: 'Stop other running tasks?',
    body:
      `Still running for this project: ${others.map(taskLabel).join(', ')}. ` +
      'Stop them too?',
    confirmLabel: 'Stop all',
    cancelLabel: 'Leave running',
    danger: true,
  });
  if (!stopAll) return;
  for (const task of others) {
    try {
      await stopOtherTask(adapter, projectId, task);
    } catch {
      // One task's stop failing must not block the rest; the task list
      // poll shows what actually stopped.
    }
  }
}
